import { dayShortText, shortDateTime, type Answer } from './types';

/**
 * Owner 2026-10-30 21:31–21:49: one point of a question's correspondence as the API sends it (fields the role may not
 * see are absent — rule 7a): `sent` the question; `answer` a decision as given (`editedAt`: re-clicked in place);
 * `change` a later change of a day (by the person, by hand, by an answer to another question); `refusal` „Odmowa” in
 * the question's event; `confirmed` „Potwierdź ręcznie”; `removed` „Usunięto z obsady”; `question` another question about
 * the days (`again`: the same event); `crew` — the answer link's neutral „Zmiana w obsadzie” (review M1).
 */
export type PointKind = 'sent' | 'answer' | 'change' | 'refusal' | 'confirmed' | 'removed' | 'question' | 'crew';
export type PointDay = { day: string; answer?: boolean; state?: 'available' | 'unavailable' | null; hours?: string };
export type TimelinePoint = {
  id: string;
  kind: PointKind;
  at?: string;
  editedAt?: string;
  message?: string;
  days?: PointDay[];
  answer?: Answer | null;
  note?: string;
  source?: 'self' | 'manual' | 'answer';
  by?: string;
  again?: boolean;
};

const TITLES: Record<PointKind, string> = {
  sent: 'Zapytanie wysłane',
  answer: 'Odpowiedź',
  change: 'Zmiana dostępności',
  refusal: 'Odmowa',
  confirmed: 'Potwierdzono ręcznie',
  removed: 'Usunięto z obsady',
  question: 'Nowe zapytanie o te dni',
  crew: 'Zmiana w obsadzie',
};
const STATE: Record<'available' | 'unavailable', string> = { available: 'dostępny', unavailable: 'niedostępny' };
const dayList = (days: readonly PointDay[] = []) => days.map((d) => dayShortText(d.day)).join(', ');

/** Who made a change: „Ty” / „sam pracownik”, „ręcznie: Szef”, „odpowiedź na inne zapytanie” (nothing the role may not see). */
function whoOf(p: TimelinePoint, mine: boolean): string {
  if (p.source === 'self') return mine ? 'Ty' : 'sam pracownik';
  if (p.source === 'manual') return p.by ? `ręcznie: ${p.by}` : 'ręcznie';
  if (p.source === 'answer') return 'odpowiedź na inne zapytanie';
  return '';
}

/**
 * A point in words: its title („Twoja odpowiedź” in „Mój kalendarz” — `mine`), who, what (the days as given or as
 * changed) and when („29.10.2099, 19:05 (zmieniono …)”).
 */
export function pointText(p: TimelinePoint, mine = false): { title: string; who: string; detail: string; when: string } {
  const when = p.at ? `${shortDateTime(p.at)}${p.editedAt ? ` (zmieniono ${shortDateTime(p.editedAt)})` : ''}` : '';
  const title = p.kind === 'answer' && mine ? 'Twoja odpowiedź' : p.kind === 'question' && p.again ? 'Ponowne zapytanie' : TITLES[p.kind];
  let detail = '';
  if (p.kind === 'sent') detail = [p.message ?? '', p.days && p.days.length > 0 ? `dni: ${dayList(p.days)}` : ''].filter((s) => s !== '').join(' — ');
  else if (p.kind === 'question') detail = p.days && p.days.length > 0 ? `dni: ${dayList(p.days)}` : '';
  else if (p.kind === 'answer') {
    const days = (p.days ?? []).map((d) => `${dayShortText(d.day)} ${d.answer ? 'tak' : 'nie'}`).join(' · ');
    detail = p.answer === 'pytanie' ? `Mam pytanie${p.note ? `: „${p.note}”` : ''}` : [days, p.note ? `„${p.note}”` : ''].filter((s) => s !== '').join(' — ');
  } else detail = (p.days ?? []).map((d) => `${dayShortText(d.day)} ${d.state === null ? 'wyczyszczono' : d.state ? `${STATE[d.state]}${d.hours ? ` ${d.hours}` : ''}` : ''}`.trim()).join(' · ');
  return { title, who: p.kind === 'sent' || p.kind === 'answer' || p.kind === 'question' ? '' : whoOf(p, mine), detail, when };
}

/** The badge's word for the newest point of a question (lower case, short). */
export function badgeWord(p: TimelinePoint): string {
  if (p.kind === 'sent') return 'czeka';
  if (p.kind === 'answer') return p.answer === 'moge' ? 'mogę' : p.answer === 'nie_moge' ? 'nie mogę' : p.answer === 'czesciowo' ? 'część dni' : p.answer === 'pytanie' ? 'pytanie' : 'odpowiada';
  return pointText(p).title.toLowerCase();
}
